import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { useFeature } from '../../stores/featureStore';
import { useAttendanceStore } from './store';
import { getMyAttendance } from './attendanceApi';
import type { MyAttendanceSession } from './attendanceApi';

// BP 23 attendance feature — public surface. Real implementation (WS-B,
// Phase 1). Export names + prop signatures ClassPage.tsx composes against are
// unchanged from the Phase 0 placeholder, per
// docs/IMPLEMENTATION-PLAN-22-27.md §6 — ClassPage never needs to change.

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Renders nothing when no attendance window is open for the current class
 * session; otherwise the code-entry card. Reads its own state (open-window
 * poll) — takes no props. */
export function CheckInCard() {
  const enabled = useFeature('class_attendance');
  const win = useAttendanceStore((s) => s.window);
  const loading = useAttendanceStore((s) => s.loading);
  const submitting = useAttendanceStore((s) => s.submitting);
  const submitError = useAttendanceStore((s) => s.submitError);
  const lastCheckin = useAttendanceStore((s) => s.lastCheckin);
  const startPolling = useAttendanceStore((s) => s.startPolling);
  const stopPolling = useAttendanceStore((s) => s.stopPolling);
  const submitCode = useAttendanceStore((s) => s.submitCode);
  const clearSubmitError = useAttendanceStore((s) => s.clearSubmitError);
  const [code, setCode] = useState('');

  useEffect(() => {
    if (!enabled) return undefined;
    startPolling();
    return () => stopPolling();
  }, [enabled, startPolling, stopPolling]);

  if (!enabled) return null;
  if (!win) {
    // While loading for the first time we don't know yet whether a window is
    // open — better to render nothing than flash the card and hide it.
    if (loading) return null;
    return null;
  }

  const already = win.my_checkin;
  const checkedIn = already || lastCheckin;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    void submitCode(code).then((ok) => { if (ok) setCode(''); });
  };

  // Loud while it needs an action, quiet the moment it does not.
  //
  // This card used to look exactly like the Learner plan and Surveys cards
  // below it — a student scanning the page had nothing telling them that this
  // one closes in ten minutes and the others do not. Attendance is the only
  // thing on /class with a deadline attached to a grade, so while a window is
  // open and this student has not checked in, the card takes an accent border
  // and a ring; once they have, it drops back to an ordinary card with a green
  // line. Nothing pulses or nags after the work is done.
  return (
    <Card className={`p-4 sm:p-5${checkedIn ? '' : ' border-ws-accent ring-2 ring-ws-accent/25'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ws-ink">
            {!checkedIn && (
              <span className="inline-flex items-center gap-1 rounded-md bg-ws-accent px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ws-on-accent">
                <span className="size-1.5 animate-pulse rounded-full bg-ws-on-accent" aria-hidden="true" />
                Open now
              </span>
            )}
            Attendance
          </h2>
          <p className="mt-0.5 text-xs text-ws-ink-3">{win.session_label || 'This class'}</p>
        </div>
      </div>

      {checkedIn ? (
        <p role="status" className="mt-3 rounded-md border border-ws-success bg-ws-success-quiet px-3 py-2 text-sm text-ws-success">
          Checked in · {formatTime('checked_in_at' in checkedIn ? checkedIn.checked_in_at : new Date().toISOString())}
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-ws-ink-2">
            Type the 6-digit code on the classroom screen. It changes every few seconds — if it
            changes while you are typing, just enter the new one.
          </p>
          <form onSubmit={onSubmit} className="mt-3 flex flex-wrap items-center gap-2">
            <label htmlFor="attendance-code" className="sr-only">Attendance code</label>
            <input
              id="attendance-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              autoFocus
              value={code}
              onChange={(e) => {
                clearSubmitError();
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              }}
              placeholder="6-digit code"
              // h-12 / text-2xl: this is typed once, in a hurry, often on a
              // phone held under a desk. Bigger target, bigger digits.
              className="h-12 min-w-0 flex-1 rounded-lg border border-ws-border bg-ws-surface px-3 text-2xl tracking-[0.3em] text-ws-ink placeholder:text-lg placeholder:tracking-normal placeholder:text-ws-ink-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
            />
            <Button type="submit" disabled={code.length !== 6} loading={submitting}>
              Check in
            </Button>
          </form>
        </>
      )}

      {submitError && (
        <p role="alert" className="mt-2 text-xs text-ws-danger">
          {submitError.message}
        </p>
      )}
    </Card>
  );
}

const STATUS_LABEL: Record<MyAttendanceSession['status'], string> = {
  present: 'Present',
  absent: 'Absent',
  no_window: '—',
};

/** A student's own attendance history across sessions. */
export function AttendanceHistory() {
  const enabled = useFeature('class_attendance');
  const [sessions, setSessions] = useState<MyAttendanceSession[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    getMyAttendance()
      .then((rows) => { if (alive) setSessions(rows); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [enabled]);

  if (!enabled) return null;
  if (error) return <p className="text-sm text-ws-danger">Could not load your attendance history.</p>;
  if (!sessions) return <Spinner label="Loading attendance…" />;
  if (sessions.length === 0) return <p className="text-sm text-ws-ink-3">No attendance-taking sessions yet.</p>;

  return (
    <div className="overflow-x-auto rounded-lg border border-ws-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-ws-border bg-ws-surface-2 text-left text-xs text-ws-ink-3">
            <th className="px-3 py-2 font-medium">Session</th>
            <th className="px-3 py-2 font-medium">Date</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Method</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.class_session_id} className="border-b border-ws-border last:border-0">
              <td className="px-3 py-1.5 text-ws-ink">{s.label}</td>
              <td className="px-3 py-1.5 text-ws-ink-3">{s.scheduled_date || '—'}</td>
              <td className={`px-3 py-1.5 ${s.status === 'present' ? 'text-ws-success' : s.status === 'absent' ? 'text-ws-danger' : 'text-ws-ink-3'}`}>
                {STATUS_LABEL[s.status]}
              </td>
              <td className="px-3 py-1.5 text-ws-ink-3">{s.method || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
